import React from 'react';

const RefundPolicyContent = () => {
  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          Refund Policy
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Last updated: April 22, 2026
        </p>
        <p className="text-gray-600 dark:text-gray-300">
          At AirBrief, we are committed to your satisfaction with our ATPL and DLR preparation materials. 
          All refund requests are handled on a case-by-case basis.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          Our Refund Approach
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-3">
          We understand that every situation is unique. Rather than enforcing strict rules, we evaluate 
          each refund request individually based on your specific circumstances.
        </p>
        <p className="text-gray-600 dark:text-gray-300">
          All purchases on AirBrief are <strong>one-time payments</strong> with no recurring charges. 
          You pay once and receive access for the specified duration.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          When to Request a Refund
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-3">
          You may request a refund if:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300 ml-4">
          <li>You experience technical issues preventing access to your purchased content</li>
          <li>The course content is significantly different from what was described</li>
          <li>You made a duplicate or accidental purchase</li>
          <li>You have billing concerns or unauthorized charges</li>
          <li>You have other legitimate concerns about your purchase</li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          How to Request a Refund
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-3">
          To request a refund, simply contact our support team:
        </p>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
          <p className="text-gray-600 dark:text-gray-300">
            <strong>Email:</strong> support@air-brief.com
          </p>
          <p className="text-gray-600 dark:text-gray-300">
            <strong>Support Portal:</strong> Available in your Profile Settings
          </p>
        </div>
        <p className="text-gray-600 dark:text-gray-300 mt-3">
          Please include:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300 ml-4">
          <li>Your order/purchase details</li>
          <li>Reason for the refund request</li>
          <li>Any relevant information about your situation</li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          What to Expect
        </h3>
        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300 ml-4">
          <li>
            <strong>Response Time:</strong> We typically respond within 24-48 hours
          </li>
          <li>
            <strong>Review Process:</strong> Each request is reviewed individually by our team
          </li>
          <li>
            <strong>Fair Assessment:</strong> We consider your circumstances and usage
          </li>
          <li>
            <strong>Processing Time:</strong> Approved refunds are processed within 5-7 business days
          </li>
          <li>
            <strong>Refund Method:</strong> Refunds are issued to your original payment method
          </li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          Technical Issues
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-3">
          If you're experiencing technical problems:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300 ml-4">
          <li>Contact us immediately at support@air-brief.com</li>
          <li>We'll work to resolve the issue as quickly as possible</li>
          <li>If we can't fix the problem, we'll discuss refund options</li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          Important Notes
        </h3>
        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300 ml-4">
          <li>
            <strong>No Recurring Charges:</strong> All purchases are one-time payments. You will never 
            be charged again unless you make another purchase.
          </li>
          <li>
            <strong>Access Duration:</strong> Your access lasts for the purchased period (e.g., 3 months, 
            6 months, 12 months, or lifetime).
          </li>
          <li>
            <strong>Digital Products:</strong> Due to the instant access nature of digital content, 
            we evaluate refunds based on usage and circumstances.
          </li>
          <li>
            <strong>Fair Use:</strong> We reserve the right to deny refunds that appear to abuse our policy.
          </li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          Contact Us
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-3">
          Have questions about refunds or need help? We're here for you.
        </p>
        <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
          <p className="text-gray-600 dark:text-gray-300">
            <strong>Email:</strong> support@air-brief.com
          </p>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            <strong>Support Portal:</strong> Log in to your account → Profile Settings → Request Help
          </p>
        </div>
      </section>

      <section className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          <strong>Our Commitment:</strong> We believe in treating each customer fairly. If you have 
          a legitimate concern about your purchase, please reach out to us. We'll work with you to 
          find a reasonable solution.
        </p>
      </section>
    </div>
  );
};

export default RefundPolicyContent;
